import React, { useMemo, useRef, useState } from 'react';
import { Box, TextField, IconButton, Typography, useTheme } from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

/**
 * Description field with a suggestion dropdown behind the bulb icon.
 * Shared by AddTimeEntryForm, EditTimeEntryForm, QuickTimeEntryForm and Timer so the
 * bulb behaves the same way in every form.
 *
 * The list has two sections, in this order: the member's recently used descriptions
 * (`suggestions`) and then the teamspace's tag names (`tagSuggestions`). Either may be
 * empty, and an empty section is omitted rather than shown with no rows.
 *
 * It is a type-ahead: whatever is in the field filters both sections by prefix, so typing
 * "l" narrows the list to descriptions starting with "l". The bulb is drawn whenever there
 * is anything to suggest and is lit (`info.main`) only while something still matches, so a
 * dim bulb means "nothing starts with what you have typed" rather than "no suggestions".
 *
 * The list opens on its own whenever the focused field has matches — the bulb toggles it,
 * it is not the only way in. Picking a row or pressing Escape dismisses the list without
 * dropping focus; typing again brings it back.
 *
 * The dropdown is rendered by us rather than by a native <datalist>: a programmatic
 * input.click() does not open a datalist picker, so the bulb used to do nothing in the
 * forms that relied on one.
 *
 * `bulbOnly` (AddTimeEntryForm, EditTimeEntryForm) switches to an explicit mode: focusing
 * or typing never opens the list, only a bulb click does, and the bulb is dim by default
 * and lit only while the list it opened is showing. Both forms autofocus the description,
 * so the default mode dropped the list over the dialog the moment it appeared. Once
 * opened, typing still filters the list live; a pick, Escape, blur or a second bulb click
 * closes it.
 *
 * Props beyond the ones listed are forwarded to the underlying TextField.
 */
const DescriptionSuggestField = ({
  bulbOnly = false,
  suggestions = [],
  tagSuggestions = [],
  onSelect,
  containerSx,
  onBlur,
  onFocus,
  onChange,
  onKeyDown,
  InputProps,
  value,
  ...textFieldProps
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  // Dismissing is remembered separately from focus so that picking a row, or pressing
  // Escape, does not immediately re-open the list under the cursor. Typing or re-focusing
  // clears it, because at that point the member is asking for suggestions again.
  const [dismissed, setDismissed] = useState(false);
  // bulbOnly: whether the bulb has the list open. Nothing but the bulb sets it.
  const [bulbOpen, setBulbOpen] = useState(false);

  // Recently used comes first: a description the member actually wrote is a better guess
  // than a bare tag name. A name that is in both sections is therefore shown once, under
  // Recently used — the same string either way, and two identical rows read as a bug.
  const sections = useMemo(() => {
    // Content-aware: once anything is typed, only offer completions of it. Prefix match,
    // not substring — the list is a type-ahead, so the row has to continue what is in the
    // field for picking it to leave the typed text intact.
    const query = String(value == null ? '' : value).trim().toLowerCase();
    const seen = new Set();
    const pick = (items) =>
      (items || []).filter((item) => {
        const key = String(item == null ? '' : item).trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        if (query && !key.startsWith(query)) return false;
        seen.add(key);
        return true;
      });
    return [
      { caption: 'Recently used', items: pick(suggestions) },
      { caption: 'Tags', items: pick(tagSuggestions) },
    ].filter((section) => section.items.length > 0);
  }, [suggestions, tagSuggestions, value]);

  // Two different questions: whether to draw the bulb at all, and whether to light it.
  const hasAnySuggestions = suggestions.length > 0 || tagSuggestions.length > 0;
  const hasMatches = sections.length > 0;

  // Default mode: the list opens by itself — focus the field and any matches are already
  // on screen. bulbOnly: only while the bulb holds it open.
  const open = bulbOnly ? focused && bulbOpen : focused && hasMatches && !dismissed;
  // bulbOnly lights the bulb for "list is open"; the default mode lights it for "something matches".
  const lit = bulbOnly ? open : hasMatches;

  return (
    <Box sx={{ position: 'relative', ...containerSx }}>
      <TextField
        label="Description (optional)"
        type="text"
        name="description"
        inputRef={inputRef}
        value={value}
        onFocus={(e) => {
          setFocused(true);
          setDismissed(false);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          // Delay so a mousedown on a suggestion still lands before the list unmounts.
          setTimeout(() => {
            setFocused(false);
            setBulbOpen(false);
          }, 150);
          onBlur?.(e);
        }}
        onChange={(e) => {
          // Typing after a pick means "show me the list again", now filtered by what is typed.
          setDismissed(false);
          onChange?.(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) {
            // Swallow it: the list is what the member is dismissing, not the dialog.
            e.stopPropagation();
            setDismissed(true);
            setBulbOpen(false);
          }
          onKeyDown?.(e);
        }}
        {...textFieldProps}
        InputProps={{
          ...InputProps,
          endAdornment: hasAnySuggestions ? (
            <IconButton
              size="small"
              tabIndex={-1}
              title="Description suggestions"
              onMouseDown={(e) => {
                e.preventDefault();
                // Driven off what is on screen, not off a flipped flag: focusing the field
                // below clears the dismissal, so a blind toggle would fight its own focus
                // handler whenever the field was not already focused.
                setDismissed(open);
                setBulbOpen(!open);
                setFocused(true);
                inputRef.current?.focus();
              }}
              sx={{
                color: lit ? theme.palette.info.main : theme.palette.text.disabled,
                // bulbOnly: hovering must not light it — lit means "open" there.
                '&:hover': { color: bulbOnly ? undefined : theme.palette.info.main },
              }}
            >
              <LightbulbIcon fontSize="small" />
            </IconButton>
          ) : InputProps?.endAdornment,
        }}
      />
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1400,
            bgcolor: 'background.paper',
            boxShadow: 2,
            borderRadius: 1,
            maxHeight: 200,
            overflow: 'auto',
          }}
        >
          {/* bulbOnly can be open with nothing matching; say so rather than show a lit bulb over nothing. */}
          {!hasMatches && (
            <Typography
              variant="caption"
              sx={{ px: 1.5, py: 1, display: 'block', color: 'text.disabled' }}
            >
              No matching suggestions
            </Typography>
          )}
          {sections.map((section) => (
            <React.Fragment key={section.caption}>
              <Typography
                variant="caption"
                sx={{ px: 1.5, pt: 1, pb: 0.25, display: 'block', color: 'text.disabled' }}
              >
                {section.caption}
              </Typography>
              {section.items.map((desc) => (
                <Box
                  key={`${section.caption}:${desc}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect?.(desc);
                    setDismissed(true);
                    setBulbOpen(false);
                  }}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {desc}
                </Box>
              ))}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DescriptionSuggestField;
